import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { BookingSummary } from "../../../types/bookingCreation";
import { setSelectedServiceType } from "../../../redux/slice/booking/bookingSlice";
import { ServiceTypeSelectorOption } from "../../../types/order";
import { useTranslation } from "react-i18next";
import { getTenantName } from "../../../util";
import useServiceStorage from "../../../hooks/useServiceStorage";
import { TenantConfigService } from "../../../types/tenantConfig";
import { serviceLabelPath } from "../serviceLabel";

const SUBSCRIPTIONS = ServiceTypeSelectorOption.SUBSCRIPTIONS;

// Get tenant from environment variable with fallback
const currentTenant = getTenantName()?.toLowerCase() || "kolula";
interface ServiceTypeSelectorItem {
  name: string;
  value: ServiceTypeSelectorOption;
  count?: number;
}

interface ServiceTypeSelectorProps {
  initialSelected: ServiceTypeSelectorOption;
  onChange?: (value: string) => void;
  bookingSummary: BookingSummary[];
  subscriptionsCount?: number;
}

const ServiceTypeSelector = ({
  initialSelected,
  bookingSummary,
  subscriptionsCount = 0,
}: ServiceTypeSelectorProps) => {
  const { selectedServiceType } = useSelector(
    (state: RootState) => state.booking
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentService, updateService } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.RENTAL
  );

  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );

  const tenantServices = tenantConfig.services;

  let tenantServiceTypes: ServiceTypeSelectorItem[] = (tenantServices || [])
    .map((value: string) => {
      if (
        Object.values(ServiceTypeSelectorOption).includes(
          value as ServiceTypeSelectorOption
        )
      ) {
        if (value !== "moreToCome") {
          let displayName = t(serviceLabelPath(value));
          
          // Special handling for STORAGE service type when tenant is segmenta
          if (value === ServiceTypeSelectorOption.STORAGE && currentTenant === "segmenta") {
            displayName = t("service_type_selector.temporary_storage_segmenta");
          }
          
          return {
            name: displayName,
            value: value as ServiceTypeSelectorOption,
          };
        }
      }
      return null;
    })
    .filter(
      (item): item is ServiceTypeSelectorItem =>
        item !== null && item.value !== ServiceTypeSelectorOption.VOUCHER && item.value !== ServiceTypeSelectorOption.MORE_TO_COME
    );

  const serviceTypes = [
    {
      name: t("service_type_selector.subscriptions", "Subscriptions"),
      value: SUBSCRIPTIONS,
      count: subscriptionsCount,
    },
    ...tenantServiceTypes,
  ];

  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);


  const handleServiceTypeClick = (
    value: ServiceTypeSelectorOption,
    index: number
  ) => {
    dispatch(setSelectedServiceType(value));
    // update the session storage with selected service type
    updateService(value);
    const selectedChip = chipRefs.current[index];
    if (selectedChip) {
      selectedChip.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "center",
      });
    }
  };

  useEffect(() => {
    let serviceType = initialSelected;
    if (currentService) {
      const currentServiceString = currentService as TenantConfigService;
      if (
        tenantServices?.length > 0 &&
        currentService &&
        tenantServices?.includes(currentServiceString)
      ) {
        serviceType = currentService;
      } else if (tenantServices?.length > 0) {
        serviceType = tenantServices[0] as ServiceTypeSelectorOption;
      }
    }
    dispatch(setSelectedServiceType(serviceType as ServiceTypeSelectorOption));
  }, [initialSelected, dispatch, currentService, tenantServices]);

  const getServiceTypeCount = (
    serviceType: ServiceTypeSelectorOption
  ): number => {
    if (serviceType === ServiceTypeSelectorOption.ALL) {
      return bookingSummary.length;
    }
    if (serviceType === ServiceTypeSelectorOption.SUBSCRIPTIONS) {
      return subscriptionsCount;
    }
    return bookingSummary.filter((booking) => booking.service === serviceType)
      .length;
  };

  const getServiceTypeCountDisplay = (count: number): string => {
    if (count > 29) {
      return "29+";
    }
    return count.toString();
  };

  useEffect(() => {
    const index = serviceTypes.findIndex(
      (type) => type.value === selectedServiceType
    );
    if (index !== -1 && chipRefs.current[index]) {
      chipRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  }, [selectedServiceType]);

  return (
    <div className="w-full max-w-[380px] mx-auto overflow-x-auto no-scrollbar fade-sides">
      <div className="flex gap-2 px-10 whitespace-nowrap">
        {serviceTypes.map((serviceType, index) => {
          const isSelected = selectedServiceType === serviceType.value;
          return (
            <div
              key={serviceType.value}
              ref={(el) => (chipRefs.current[index] = el)}
              onClick={() => handleServiceTypeClick(serviceType.value, index)}
              className={`flex items-center gap-2 rounded-full py-2 px-3 text-sm cursor-pointer select-none
            ${
              isSelected
                ? "bg-primary/10 text-foreground/90"
                : "hover:bg-primary/10"
            }`}
            >
              <span>{serviceType.name}</span>
              {getServiceTypeCount(serviceType.value) > 0 && (
                <span
                  className={`${
                    currentTenant !== "kolula"
                      ? "text-background"
                      : "text-foreground/90"
                  } bg-primary text-[8px] font-semibold flex items-center justify-center rounded p-1 w-5 h-4 leading-[10px]`}
                >
                  {getServiceTypeCountDisplay(
                    getServiceTypeCount(serviceType.value)
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceTypeSelector;
